import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismAccordion,
  PrismBadge,
  PrismBanner,
  PrismCheckbox,
  PrismIcon,
  PrismNumberInput,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq, or } from 'ember-truth-helpers';
import ExceptionIndicator from 'lumen/components/hotel/pricing-assistant/exception-indicator';
import {
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
  NUMBER_INPUT_MAX_VALUE,
  OVERRIDE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './stay-restrictions-accordion.module.css';

export interface StayRestrictionsAccordionState {
  automationOn: boolean;
  currentLabel: string;
  errorMessage: string | null;
  failedValue: number | null;
  formattedValue: string;
  hasOverride: boolean;
  isLoading: boolean;
  onBlur: () => void;
  onTypeChange: (event: Event) => void;
  restrictionType: MinLosRestrictionType;
  showException: boolean;
  toggleAutomation: (event: Event) => void;
  touched: boolean;
  updateValue: (value: number | null) => void;
  value: number | null;
  warningMessage?: string | null;
}

interface StayRestrictionsAccordionSignature {
  Args: {
    /* Namespaces the radio group name and every data-test-id, so two accordions
       can render side by side without their radios linking into one group. */
    id: string;
    title: string;
  } & (
    | {
        /* When true, the accordion body renders the yielded emptyState block
           instead of the automation/restriction controls, so no state is needed. */
        showEmptyState: true;
        state?: undefined;
      }
    | {
        showEmptyState?: false;
        state: StayRestrictionsAccordionState;
      }
  );
  Blocks: {
    emptyState: [];
    settingsLink: [];
  };
}

const StayRestrictionsAccordion = <template>
  <PrismAccordion
    @initiallyOpened={{true}}
    @title={{@title}}
    class="mx-500 my-400"
    data-test-id="{{@id}}-accordion"
  >
    <:summaryAfter>
      {{#if @state.showException}}
        <ExceptionIndicator />
      {{else if @state.hasOverride}}
        <PrismBadge
          @iconName={{getOverrideVariant OVERRIDE_TYPES.MIN_LOS}}
          @label={{t "pricing_manager.automation_off"}}
          @type="warning"
        />
      {{/if}}
    </:summaryAfter>
    <:default>
      {{#if @showEmptyState}}
        {{yield to="emptyState"}}
      {{else}}
        {{#if @state.showException}}
          <div class="mb-400">
            <PrismBanner
              @bannerMessage={{t
                "pricing_assistant.push_error_code.generic_issue"
              }}
              @bannerStyle="critical"
              @bannerTitle={{t
                "pricing_assistant.exception.action_state.restriction_title_with_value"
                value=@state.failedValue
              }}
              @icon="circle-minus"
            />
          </div>
        {{/if}}
        <div
          class="{{styles.automation-container}}
            mb-400 flex items-center justify-between p-400"
        >
          <PrismCheckbox
            @checked={{@state.automationOn}}
            @disabled={{@state.isLoading}}
            @onChange={{@state.toggleAutomation}}
            data-test-id="{{@id}}-automation-checkbox"
          >
            <p class={{if @state.automationOn styles.automation-label-on}}>
              {{t "pricing_manager.automation_on"}}
            </p>
          </PrismCheckbox>
          {{#if @state.isLoading}}
            <div
              class="loading-pulse w-[120px]"
              data-test-id="{{@id}}-loading"
            ></div>
          {{else}}
            <div
              class="flex flex-col items-end
                {{unless @state.automationOn styles.automation-value-off}}"
            >
              <div class="flex items-center gap-100">
                <PrismIcon
                  @color={{if @state.automationOn "on-intelligence" "disabled"}}
                  @name={{if @state.automationOn "automation" "automation-off"}}
                  @size="small"
                />
                <p
                  class="text-300--regular"
                  data-test-id="{{@id}}-automation-value"
                >
                  {{@state.formattedValue}}
                </p>
              </div>
              <p
                class="text-200--regular {{styles.current-label}}"
                data-test-id="{{@id}}-current-value"
              >
                {{@state.currentLabel}}
              </p>
            </div>
          {{/if}}
        </div>

        {{#unless (or @state.automationOn @state.isLoading)}}
          <div class={{styles.divider}} />
          <div class="mt-400 flex flex-col gap-200">
            <PrismRadioGroup
              @isHorizontal={{true}}
              @name="{{@id}}-type"
              @onChange={{@state.onTypeChange}}
              @value={{@state.restrictionType}}
              class={{styles.radio-group}}
              as |RadioGroup|
            >
              <RadioGroup.input
                @value={{MIN_LOS_RESTRICTION_TYPE.MIN_LOS}}
                class="items-center"
              >
                <div class="flex items-center gap-200">
                  {{t "pricing_manager.restrictions.min_los_label"}}
                  <div class={{styles.min-los-input}}>
                    <PrismNumberInput
                      @disabled={{eq
                        @state.restrictionType
                        MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
                      }}
                      @hasErrors={{if @state.errorMessage true false}}
                      @hasWarnings={{if
                        (and
                          @state.warningMessage
                          (eq
                            @state.restrictionType
                            MIN_LOS_RESTRICTION_TYPE.MIN_LOS
                          )
                        )
                        true
                      }}
                      @inlineLabel={{t "pricing_manager.restrictions.nights"}}
                      @isTouched={{@state.touched}}
                      @max={{NUMBER_INPUT_MAX_VALUE}}
                      @min={{0}}
                      @onValueChange={{@state.updateValue}}
                      @removePadding={{true}}
                      @required={{eq
                        @state.restrictionType
                        MIN_LOS_RESTRICTION_TYPE.MIN_LOS
                      }}
                      @value={{@state.value}}
                      data-test-id="{{@id}}-value-input"
                      {{on "blur" @state.onBlur}}
                    />
                  </div>
                </div>
              </RadioGroup.input>
              <RadioGroup.input
                @value={{MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION}}
              >
                {{t "pricing_manager.restrictions.no_min_los"}}
              </RadioGroup.input>
            </PrismRadioGroup>
            {{#if @state.errorMessage}}
              <div class={{styles.error-message}} data-test-id="{{@id}}-error">
                <PrismIcon
                  @color="critical"
                  @name="circle-minus"
                  @size="small"
                />
                <p class="text-200--medium">{{@state.errorMessage}}</p>
              </div>
            {{/if}}
            {{#if @state.warningMessage}}
              <div
                class={{styles.warning-message}}
                data-test-id="{{@id}}-warning"
              >
                <PrismIcon @color="warning" @name="warning" @size="small" />
                <p class="text-200--medium">{{@state.warningMessage}}</p>
              </div>
            {{/if}}
          </div>
        {{/unless}}

        {{#if (has-block "settingsLink")}}
          <div class={{unless @state.automationOn "mt-400"}}>
            {{yield to="settingsLink"}}
          </div>
        {{/if}}
      {{/if}}
    </:default>
  </PrismAccordion>
</template> satisfies TOC<StayRestrictionsAccordionSignature>;

export default StayRestrictionsAccordion;
